import { useEffect, useState } from 'react'
import { formatNoteWhen, sanitizeNoteHtml } from '@/lib/utils/noteFormatting'
import { FileText, Mail, MessageSquare } from 'lucide-react'
import { EditNoteModal } from '@/components/notes/EditNoteModal'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import detailStyles from '@/features/reimbursements/styles/reimbursementDetail.module.css'
import { showSuccess } from '@/lib/utils/notifications'
import { useUserRole } from '@/hooks/useUserRole'
import type { PublicNoteRow } from '../../hooks/usePublicNotesAndMessages'
import type { ResidentPublicNotesMessagesCardProps } from '../../types'
import {
  canEditPublicDefaultNote,
  getPublicNoteAuthorName,
  getPublicNoteContent,
  getPublicNoteEmailSendTo,
  getPublicNoteRowId,
} from '../../utils/residentPublicNotesHelpers'

/**
 * Angular `resident-edit-new` “Notes & Messages (publicly visible)” block on the
 * Default / reimbursement tab: header + New Message + merged notes/history list.
 */
export function ResidentPublicNotesMessagesCard({
  register,
  messageValue,
  mergedRows,
  loading,
  saving,
  disabled,
  showMessageBox,
  showSaveButton = true,
  messageReadOnly = false,
  onSave,
  onNotesUpdated,
}: ResidentPublicNotesMessagesCardProps) {
  const { user, isLibertyRole } = useUserRole()
  const currentUserId = user?.user_id ?? null
  const [editingNote, setEditingNote] = useState<PublicNoteRow | null>(null)
  const [editClock, setEditClock] = useState(0)

  useEffect(() => {
    const id = window.setInterval(() => setEditClock((value) => value + 1), 30_000)
    return () => window.clearInterval(id)
  }, [])

  const trimmed = messageValue.trim()
  const composeLocked = disabled || messageReadOnly
  const saveDisabled = !trimmed || saving || composeLocked
  // eslint-disable-next-line react-hooks/purity -- intentional: reads current time at render to gate the public-note edit eligibility window
  const now = Date.now()

  return (
    <section
      className={`mt-6 ${detailStyles.section}`}
      data-testid="resident-public-notes-card"
    >
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className={detailStyles.sectionTitle}>
          <span className={detailStyles.sectionTitleIcon} aria-hidden="true">
            <MessageSquare className="h-4 w-4" />
          </span>
          Notes & Messages (publicly visible)
        </h2>
        {showSaveButton ? (
          <button
            type="button"
            onClick={() => void onSave()}
            disabled={saveDisabled}
            data-testid="resident-public-notes-save-button"
            className={primaryEntityActionButtonClass}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        ) : null}
      </div>

      {showMessageBox ? (
        <label className="mb-4 block text-sm font-medium text-slate-800">
          New Message
          <textarea
            className="mt-1 min-h-28 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-button focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50 read-only:bg-slate-50"
            rows={4}
            disabled={disabled}
            readOnly={messageReadOnly}
            data-testid="resident-public-notes-message-textarea"
            {...register('message')}
          />
        </label>
      ) : (
        <input type="hidden" {...register('message')} />
      )}

      {loading ? (
        <p
          className="rounded-lg border border-sky-100 bg-sky-50/80 px-4 py-6 text-center text-sm text-slate-600"
          data-testid="resident-public-notes-loading"
        >
          Loading messages…
        </p>
      ) : mergedRows.length ? (
        <div
          className="rounded-lg border border-sky-100 bg-sky-50/80 px-2 py-3 sm:px-4"
          data-testid="resident-public-notes-list"
        >
          <ul className="max-h-[calc(10*4.75rem)] space-y-0 divide-y divide-sky-100/80 overflow-y-auto overscroll-contain">
            {mergedRows.map((row, index) => {
              const key = String(row.note_id ?? row.message_id ?? row.id ?? index)
              const name = getPublicNoteAuthorName(row)
              const isNoteType = row.type === 'notes' || String(row.email_send) === '0'
              const html =
                row.is_system_html === '1' || row.is_system_html === true || row.is_system_html === 1
              const content = getPublicNoteContent(row)
              const updated = row.updated_at
              const emailSendTo = getPublicNoteEmailSendTo(row)
              const editable =
                editClock >= 0 &&
                canEditPublicDefaultNote(row, { isLibertyRole, currentUserId, now })
              const editableNoteId = getPublicNoteRowId(row)
              return (
                <li
                  key={key}
                  className="grid grid-cols-1 gap-3 py-3 text-sm text-slate-700 first:pt-1 sm:grid-cols-12 sm:gap-0"
                  data-testid={`resident-public-notes-row-${key}`}
                >
                  {/* Angular col-lg-3: author + timestamp */}
                  <div className="flex gap-2 border-sky-200 sm:col-span-3 sm:border-r sm:pr-3">
                    <span className="mt-0.5 shrink-0 text-slate-500" aria-hidden>
                      {isNoteType ? <FileText className="h-4 w-4" /> : <Mail className="h-4 w-4" />}
                    </span>
                    <div className="min-w-0">
                      <div className="text-[13px] font-semibold text-slate-800">{name}</div>
                      <div className="mt-1 text-xs text-slate-600">{formatNoteWhen(row.created_at)}</div>
                      {updated ? (
                        <div className="mt-0.5 text-xs font-medium text-red-600">
                          Modified {formatNoteWhen(updated)}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  {/* Angular col-lg-1: email_send_to (note destination / type) */}
                  <div
                    className="min-w-0 break-words border-sky-200 px-0 text-xs font-medium text-slate-800 sm:col-span-2 sm:border-r sm:px-3"
                    data-testid={`resident-public-notes-email-send-to-${key}`}
                  >
                    {emailSendTo || '\u00A0'}
                  </div>
                  {/* Angular col-lg-8: message body */}
                  <div className="min-w-0 pl-0 sm:col-span-7 sm:pl-4 sm:pr-2">
                    {html ? (
                      <div
                        className="notes-preline whitespace-pre-wrap break-words text-slate-800 [&_a]:text-[rgb(21_151_255)]"
                        dangerouslySetInnerHTML={{ __html: sanitizeNoteHtml(content) }}
                      />
                    ) : (
                      <span className="whitespace-pre-wrap break-words text-slate-800">{content}</span>
                    )}
                    {editable ? (
                      <button
                        type="button"
                        onClick={() => setEditingNote(row)}
                        disabled={editableNoteId == null}
                        title="Edit note"
                        data-testid={`resident-public-notes-edit-btn-${editableNoteId ?? key}`}
                        className="ml-3 text-sm font-semibold text-blue-700 underline disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Edit
                      </button>
                    ) : null}
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      ) : (
        <div
          className="rounded-lg border border-sky-100 bg-sky-50/80 px-4 py-6 text-center text-sm text-slate-500"
          data-testid="resident-public-notes-empty"
        >
          No Messages
        </div>
      )}

      <EditNoteModal
        isOpen={!!editingNote}
        notesId={editingNote ? getPublicNoteRowId(editingNote) : null}
        initialNotes={editingNote ? getPublicNoteContent(editingNote) : ''}
        onClose={() => setEditingNote(null)}
        onSaved={() => {
          showSuccess('Note Edited Successfully!')
          setEditingNote(null)
          void onNotesUpdated?.()
        }}
        testIdPrefix="resident-public-notes-edit"
      />
    </section>
  )
}
